import Vue from 'vue';
import { db, functions } from '@/firebase';
import type { DocumentReference } from '@/firebase';
import { ROUTER } from '@/router/constants';
import { mapActions, mapState } from 'vuex';
import {
	Avatar,
	Runnit,
	RUNNIT_TYPE,
	RunnitNodeDef,
	RUNNITS_ACCESS_LEVEL,
	RunnitType,
} from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { noteBoardsAdded } from '@/utils/boardsMirrorLag';
import { JUST_ADDED_LIST, noteJustAdded } from '@/utils/justAdded';
import { justAddedBoardRow } from '@/views/Runnits/pages/RunnitBoardsPage/RunnitBoardsDashboard/boardsListState';
import moment from 'moment-timezone';
import _sampleSize from 'lodash/sampleSize';
import _trim from 'lodash/trim';

export const RunnitsCRUDMixin = Vue.extend({
	computed: {
		...mapState([
			'user',
			'team',
			'limitNumRunnits',
			'runnitState',
		]),
	},
	methods: {
		/**
		 * A board just created is pinned on top of the Boards list it belongs to (its
		 * Team's, or its owner's own) until the mirror has it (utils/justAdded.ts).
		 */
		noteBoardJustAdded (runnit: Partial<Runnit> & { id: string }) {
			const ownerId: string | null = runnit.userId || (this.user && this.user.id) || null;
			const listKey: string | null = runnit.teamId
				? JUST_ADDED_LIST.teamBoards(runnit.teamId)
				: (ownerId ? JUST_ADDED_LIST.personalBoards(ownerId) : null);
			noteJustAdded(listKey, [{
				id: runnit.id,
				row: justAddedBoardRow({ ...runnit, userId: ownerId } as Runnit, moment().toISOString()),
			}]);
		},
		...mapActions([
			'updateLoader',
			'updateSnackbar',
			'updateRunnitState',
			'setUpsellDialog',
			'setPendingRunnit',
		]),
		onEditRunnitSettings (runnit: Runnit) {
			if (runnit) {
				this.updateRunnitState({
					runnitDraft: null,
					runnitDraftNodeRef: null,
					runnit,
					configureRunnitOpen: true,
				});
			}
		},
		async onOfferingCardClick (runnitId: string, runnitNodeDef: RunnitNodeDef, queryParams: Record<string, any> = {}, routeToBoard: boolean = true, runnitType?: RunnitType | null) {
			if (runnitId && this._get(runnitNodeDef, 'id')) {
				const addNodeToRunnitFunctionRef = functions
					.httpsCallable('addNodeToRunnit');
				const { success } = (await addNodeToRunnitFunctionRef({
					runnitId: runnitId,
					nodeDefId: runnitNodeDef.id,
				})).data;

				if (!success) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'Error, problem adding your Board, please try again. If the problem continues please reach out to us with a support ticket',
						show: true,
					});
					return;
				}

				await this.updateLoader({
					show: false,
					message: null,
				});
			}
			if (routeToBoard) {
				// Pre-set the type so RunnitBoard can pick the right loading
				// shape before the Firestore doc binds. Paired with id so a
				// stale value can't bleed into the next nav.
				if (runnitType) this.setPendingRunnit({ id: runnitId, type: runnitType });
				await this.routerPush(this.$route, this.$router, {
					name: ROUTER.RUNNITS_BOARD,
					params: {
						runnitId: runnitId,
					},
					query: queryParams,
				});
			}
		},
		async createRunnitDraft (runnitNodeDef, type: RunnitType) {
			const chosenRandomAvatar = await this.getRandomDefaultAvatar();
			const nowDate: Date = new Date();
			let title: string = '';
			if (runnitNodeDef && runnitNodeDef.title) {
				title = type === RUNNIT_TYPE.SINGLE_TOOL ? runnitNodeDef.title : `My ${runnitNodeDef.title}`
			}

			return {
				createdAt: nowDate,
				deletedAt: null,
				userId: this.user.id,
				...(this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? {
					teamId: this.team.id,
					accessLevel: this.runnitState.runnitsAccessLevel || RUNNITS_ACCESS_LEVEL.PRIVATE,
				} : {
					teamId: null,
					accessLevel: null,
				}),
				title,
				description: null,
				...(chosenRandomAvatar ? {
					avatarId: chosenRandomAvatar.id,
					avatar: chosenRandomAvatar,
				} : {}),
			};
		},
		canCreateNewRunnit (isSingleTool = false) {
			if (isSingleTool) return true;
			if (
				this.loadingLimitNumRunnits ||
				(
					this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
					!this.team
				)
			) {
				return false;
			}

			if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
				return true;
			}

			const unlimitedRunnits: boolean = !!this._get(this.user, 'clubInfo.limit.unlimitedRunnits');
			const numRunnitsLimit: number = this._get(this.user, 'clubInfo.limit.numRunnitsLimit') || 1;
			const tooManyRunnits: boolean = !unlimitedRunnits && this.limitNumRunnits.length >= numRunnitsLimit;

			// TODO: Vuex issues
			if (
				!this.user.isAdmin &&
				tooManyRunnits
			) {
				this.setUpsellDialog({ runnitIsAtNumRunnitsLimit: true });
				return false;
			}

			return true;
		},
		async createNewRunnit (runnitDraft: Runnit, runnitNodeDef: RunnitNodeDef, type: RunnitType, queryParams: Record<string, any> = {}, routeToBoard: boolean = true): Promise<Runnit | null> {
			if (!this.canCreateNewRunnit(type === RUNNIT_TYPE.SINGLE_TOOL)) return null;

			if (type === RUNNIT_TYPE.SINGLE_TOOL && !runnitNodeDef) {
				console.error('Error opening single use tool, tool not defined');

				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error, opening the tool, please try again. If the problem continues please reach out to us with a support ticket.',
					show: true,
				});
				return null;
			}

			let createdRunnit: Runnit | null = null;

			try {
				let message = `Adding a Board${this._get(runnitNodeDef, 'title') ? ` with ${this._get(runnitNodeDef, 'title')}` : ''}, please wait...`;
				if (type === RUNNIT_TYPE.SINGLE_TOOL) {
					message = `Setting ${this._get(runnitNodeDef, 'title')} up, please wait...`
				}
				await this.updateLoader({
					show: true,
					message,
				});
				this.addingNewRunnit = true;

				// Always remove isFeatured from draft - it should only be set manually by admins after creation
				const cleanedDraft: Partial<Runnit> = runnitDraft ? (() => {
					const { isFeatured, ...rest } = runnitDraft;
					return rest;
				})() : {};

				const runnitData: Partial<Runnit> = {
					...cleanedDraft,
					// Ensure userId matches the current authenticated user (required by Firestore rules)
					userId: this.user.id,
					...(cleanedDraft.avatarId ? {
						avatarId: cleanedDraft.avatarId,
						avatar: db.doc(`avatars/${cleanedDraft.avatarId}`)
					} : {
						avatarId: null,
						avatar: null,
					}),
					type: type || RUNNIT_TYPE.WORKFLOW,
					...(type === RUNNIT_TYPE.SINGLE_TOOL && {
						singleToolNodeDefId: runnitNodeDef.id,
						teamId: null,
						accessLevel: null,
					}),
				} as unknown as Partial<Runnit>;

				const addedRunnitRef: DocumentReference = await db
					.collection('runnits')
					.add(runnitData);

				// The Boards page lists from the mirror, which has this board only a few seconds from now,
				// so it is pinned on top of the list until then
				if (addedRunnitRef?.id && runnitData.type === RUNNIT_TYPE.WORKFLOW) {
					this.noteBoardJustAdded({ ...runnitData, id: addedRunnitRef.id });
					noteBoardsAdded();
				}

				if (addedRunnitRef?.id && type !== RUNNIT_TYPE.TEMPLATE) {
					await this.onOfferingCardClick(addedRunnitRef.id, runnitNodeDef, queryParams, routeToBoard, type);

					// Store the created runnit to return after cleanup
					createdRunnit = {
						...runnitData,
						id: addedRunnitRef.id,
					} as Runnit;
				}
			} catch (e) {
				console.error('Error adding runnit', e);

				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error, problem adding your Board, please try again. If the problem continues please reach out to us with a support ticket',
					show: true,
				});
			} finally {
				this.addingNewRunnit = false;

				this.updateRunnitState({
					runnitDraft: null,
					runnitDraftNodeRef: null,
				});

				await this.updateLoader({
					show: false,
					message: null,
				});
			}

			return createdRunnit;
		},
		async findMatchingSingleToolRunnit (runnitNodeDef: RunnitNodeDef) {
			const runnitRef = db.collection('runnits')
				.where('type', '==', RUNNIT_TYPE.SINGLE_TOOL)
				.where('singleToolNodeDefId', '==', runnitNodeDef.id)
				.where('userId', '==', this.user.id)
				.where('teamId', '==', null)
				.where('deletedAt', '==', null)
				.limit(1);

			let singleToolRunnit: Runnit = null;
			(await runnitRef.get()).forEach((doc: any) => {
				if (singleToolRunnit) return;
				singleToolRunnit = {
					...doc.data(),
					get id () { return doc.id },
				} as Runnit;
			});

			// Update runnit title if it differs from node def (single tool runnit only)
			const nodeDefTitle: string = _trim(runnitNodeDef.title);
			if (singleToolRunnit && _trim(singleToolRunnit.title) !== nodeDefTitle) {
				singleToolRunnit.title = nodeDefTitle;
				await db.collection('runnits').doc(singleToolRunnit.id).update({
					title: nodeDefTitle,
				});
			}

			return singleToolRunnit;
		},
		async ensureSingleToolNodeExists (singleToolRunnit: Runnit, runnitNodeDef: RunnitNodeDef) {
			try {
				if (!singleToolRunnit?.id || !runnitNodeDef?.id) return;
				// Check if the expected node already exists on the runnit
				const existingNodeSnapshot = await db
					.collection(`runnits/${singleToolRunnit.id}/runnitNodes`)
					.where('deletedAt', '==', null)
					.where('nodeDefId', '==', runnitNodeDef.id)
					.limit(1)
					.get();

				if (!existingNodeSnapshot.empty) return; // Node already exists so don't create it

				// Create the node via CF to also create private node
				const addNodeToRunnitFunctionRef = functions.httpsCallable('addNodeToRunnit');
				const { success } = (await addNodeToRunnitFunctionRef({
					runnitId: singleToolRunnit.id,
					nodeDefId: runnitNodeDef.id,
				})).data;

				if (!success) {
					console.error('ensureSingleToolNodeExists error', 'addNodeToRunnit returned success: false');
				}
			} catch (e) {
				console.error('ensureSingleToolNodeExists error', e);
			}
		},
		async onAddRunnitClick (runnitNodeDef: RunnitNodeDef, type: RunnitType = RUNNIT_TYPE.WORKFLOW, queryParams: Record<string, any> = {}) {
			if (type === RUNNIT_TYPE.SINGLE_TOOL) {
				const singleToolRunnit: Runnit = await this.findMatchingSingleToolRunnit(runnitNodeDef);

				if (singleToolRunnit) {
					// Self-heal: ensure the single tool node exists before navigating
					await this.ensureSingleToolNodeExists(singleToolRunnit, runnitNodeDef);

					this.setPendingRunnit({ id: singleToolRunnit.id, type: RUNNIT_TYPE.SINGLE_TOOL });
					await this.routerPush(this.$route, this.$router, {
						name: ROUTER.RUNNITS_BOARD,
						params: {
							runnitId: singleToolRunnit.id,
						},
						query: queryParams,
					});
					return;
				}

				// create a new single tool runnit
				const runnitDraft = await this.createRunnitDraft(runnitNodeDef, type);
				await this.createNewRunnit(runnitDraft, runnitNodeDef, RUNNIT_TYPE.SINGLE_TOOL, queryParams, true);
				return;
			}

			if (!this.canCreateNewRunnit(type === RUNNIT_TYPE.SINGLE_TOOL)) return;

			const runnitDraft = await this.createRunnitDraft(runnitNodeDef, type);

			this.updateRunnitState({
				runnitDraft,
				runnitDraftNodeRef: runnitNodeDef,
				runnit: null,
				configureRunnitOpen: true,
			});
		},
		async createRunnitFromTemplate (templateRunnit: Runnit, runnitDraft?: Partial<Runnit>, routeToBoard: boolean = true): Promise<Runnit | null> {
			if (!this.canCreateNewRunnit(runnitDraft?.type === RUNNIT_TYPE.SINGLE_TOOL)) return null;

			let createdRunnit: Runnit | null = null;

			try {
				await this.updateLoader({
					show: true,
					message: `Creating board from template "${templateRunnit.title}", please wait...`,
				});

				// Use provided runnitDraft or create a default one based on the template
				const finalRunnitDraft: Partial<Runnit> = runnitDraft || {
					...templateRunnit,
					title: `${templateRunnit.title} (Copy)`,
					userId: this.user.id,
					createdAt: null, // Let Firestore handle the timestamp
					deletedAt: null,
					// Reset template-specific fields
					type: RUNNIT_TYPE.WORKFLOW,
					accessLevel: RUNNITS_ACCESS_LEVEL.PRIVATE,
					teamId: this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? this.team?.id : null,
					// Handle avatar reference properly - keep the original avatar object
					avatarId: templateRunnit.avatarId,
					avatar: templateRunnit.avatar,
				};

				const createRunnitFromTemplateRef = functions.httpsCallable('createRunnitFromTemplate');
				const payload = {
					templateId: templateRunnit.id,
					runnit: {
						title: finalRunnitDraft.title,
						description: finalRunnitDraft.description,
						onboardingData: finalRunnitDraft.onboardingData,
						avatarId: finalRunnitDraft.avatarId || null,
						teamId: finalRunnitDraft.teamId || null,
						customFields: finalRunnitDraft.customFields || {},
					},
				};

				const { data } = await createRunnitFromTemplateRef(payload);
				if (!data?.success || !data?.runnitId) {
					throw new Error(data?.error || 'createRunnitFromTemplate did not return success');
				}

				createdRunnit = {
					...finalRunnitDraft,
					id: data.runnitId,
				} as Runnit;
				this.noteBoardJustAdded(createdRunnit);
				noteBoardsAdded();

				if (routeToBoard) {
					// Navigate to the newly created board
					this.setPendingRunnit({ id: createdRunnit.id, type: RUNNIT_TYPE.WORKFLOW });
					await this.routerPush(this.$route, this.$router, {
						name: ROUTER.RUNNITS_BOARD,
						params: {
							runnitId: createdRunnit.id,
						},
					});
				}
			} catch (e) {
				console.error('Error creating runnit from template', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error creating board from template, please try again',
					show: true,
				});
			} finally {
				await this.updateLoader({
					show: false,
					message: null,
				});
			}

			return createdRunnit;
		},
		async getRandomDefaultAvatar () {
			const avatars = [];
			try {
				const avatarsRef = db.collection('avatars')
					.where('deletedAt', '==', null)
					.where('userId', '==', 'DEFAULT')
					.where('useCase', '==', 'RUNNIT_LIB')
					.where('name', '>=', '901_runnitDefaults-316.png') // Only allow users to pick new avatar images
					.orderBy('name', 'asc');

				(await avatarsRef.get()).forEach(async (doc: any) => {
					avatars.push({
						...doc.data(),
						get id () { return doc.id },
					} as Avatar);
				});
			} catch (err) {
				console.error('failed to get default avatar images', err);
			}
			return avatars.length ? _sampleSize(avatars, 1)[0] : null;
		},
	},
});
